import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { SliderComponent } from '@examdojo/core/widget';
import katex from 'katex';
import { normalInv, normalInvGeneral, normalPdf } from '../../../shared/normal-dist-math.util';
import { NormalDistributionChartComponent } from '../normal-distribution-chart';
import { NormalDistributionChartParams } from '../normal-distribution-chart/normal-distribution-chart.model';

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html' });
}

export interface InvNormLabParams {
  initialMu: number;
  initialSigma: number;
  initialP: number;
  chartHeight?: number;
}

const DEFAULT_PARAMS: InvNormLabParams = {
  initialMu: 100,
  initialSigma: 26,
  initialP: 0.86,
  chartHeight: 300,
};

/** Use Tailwind gray-400 so adaptEchartsOptions remaps it for dark mode. */
const CURVE_COLOR = '#9ca3af';
const NEUTRAL_500 = '#6b7280';
const PINK = '#db2777';
const PINK_FILL = 'rgba(236, 72, 153, 0.25)';
/** sky-500 — matches the complement shaded area. */
const SKY_LABEL = '#0ea5e9';
const ORANGE = '\\color{#f97316}';
const INDIGO = '\\color{#6366f1}';

@Component({
  selector: 'dojo-inv-norm-lab',
  standalone: true,
  imports: [NormalDistributionChartComponent, SliderComponent, MatButtonToggleModule],
  templateUrl: './inv-norm-lab.component.html',
  styleUrl: './inv-norm-lab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class InvNormLabComponent {
  constructor() {
    effect(
      () => {
        const p = this.params();
        this.mu.set(p.initialMu);
        this.sigma.set(p.initialSigma);
        this.p.set(p.initialP);
      },
      { allowSignalWrites: true },
    );
  }

  private readonly sanitizer = inject(DomSanitizer);

  readonly params = input<InvNormLabParams>(DEFAULT_PARAMS);

  readonly mu = signal(DEFAULT_PARAMS.initialMu);
  readonly sigma = signal(DEFAULT_PARAMS.initialSigma);
  readonly p = signal(DEFAULT_PARAMS.initialP);
  readonly tail = signal<'left' | 'right'>('left');

  /** The effective probability for the LEFT tail (used for invNorm) */
  readonly pLeft = computed(() => (this.tail() === 'left' ? this.p() : 1 - this.p()));

  /** z-value from inverse standard normal */
  readonly zValue = computed(() => normalInv(this.pLeft()));

  /** k = the boundary value */
  readonly k = computed(() => normalInvGeneral(this.pLeft(), this.mu(), this.sigma()));

  readonly chartHeight = computed(() => this.params().chartHeight ?? 300);

  readonly chartParams = computed<NormalDistributionChartParams>(() => {
    const mu = this.mu();
    const sigma = this.sigma();
    const k = this.k();
    const p = this.p();
    const tail = this.tail();
    const xMin = mu - 4 * sigma;
    const xMax = mu + 4 * sigma;
    const peak = normalPdf(mu, mu, sigma);

    const shadedFrom = tail === 'left' ? xMin : k;
    const shadedTo = tail === 'left' ? k : xMax;

    // Labels sit at 55% peak height, symmetrically offset from the k dashed line.
    // p is on the shaded side, 1-p on the complement side — both 12px from the line.
    const labelY = peak * 0.55;
    const GAP = 12;

    return {
      curves: [{ mu, sigma, color: CURVE_COLOR }],
      xMin: Math.floor(xMin),
      xMax: Math.ceil(xMax),
      gridRight: 20,
      shadedRegions: [{ from: shadedFrom, to: shadedTo, color: PINK_FILL }],
      shadeComplement: true,
      topLabels: [
        {
          value: k,
          text: `k = ${fmt(k)}`,
          lineType: 'dashed',
          lineWidth: 1.5,
          color: NEUTRAL_500,
          extent: 'full',
        },
      ],
      annotations: [
        // p label: shaded side of the line, text hugs toward the line
        {
          x: k,
          y: labelY,
          dx: tail === 'left' ? -GAP : GAP,
          align: tail === 'left' ? 'right' : 'left',
          text: `p = ${fmt4(p)}`,
          color: PINK,
        },
        // 1-p label: complement side of the line, text hugs toward the line
        {
          x: k,
          y: labelY,
          dx: tail === 'left' ? GAP : -GAP,
          align: tail === 'left' ? 'left' : 'right',
          text: `1\\text{-}p = ${fmt4(1 - p)}`,
          color: SKY_LABEL,
        },
      ],
      title: {
        text: `X \\sim N(${fmt(mu)},\\, ${fmt(sigma)}^2)`,
        color: NEUTRAL_500,
      },
    };
  });

  readonly zFormulaHtml = computed<SafeHtml>(() => {
    const pVal = fmt4(this.p());
    const zVal = fmt3(this.zValue());
    const tailLabel = this.tail() === 'left' ? pVal : `1 - ${pVal}`;
    const latex = `z = \\texttt{invNorm}(${tailLabel}) = ${zVal}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly kFormulaHtml = computed<SafeHtml>(() => {
    const muVal = fmt(this.mu());
    const sigmaVal = fmt(this.sigma());
    const zVal = fmt3(this.zValue());
    const kVal = fmt(this.k());
    const latex = `k = {${ORANGE}\\mu} + z \\cdot {${INDIGO}\\sigma} = {${ORANGE}${muVal}} + ${zVal} \\times {${INDIGO}${sigmaVal}} = ${kVal}`;
    return this.sanitizer.bypassSecurityTrustHtml(renderLatex(latex));
  });

  readonly muLabelHtml = this.sanitizer.bypassSecurityTrustHtml(renderLatex('\\mu'));
  readonly sigmaLabelHtml = this.sanitizer.bypassSecurityTrustHtml(renderLatex('\\sigma'));
  readonly pLabelHtml = this.sanitizer.bypassSecurityTrustHtml(renderLatex('p'));

  readonly leftTailHtml = this.sanitizer.bypassSecurityTrustHtml(`Left tail ${renderLatex('P(X \\leq k)')}`);
  readonly rightTailHtml = this.sanitizer.bypassSecurityTrustHtml(`Right tail ${renderLatex('P(X \\geq k)')}`);

  readonly setTail = (tail: 'left' | 'right'): void => {
    this.tail.set(tail);
  };

  readonly onMuChange = (value: number): void => {
    this.mu.set(value);
  };

  readonly onSigmaChange = (value: number): void => {
    this.sigma.set(Math.max(0.5, value));
  };

  readonly onPChange = (value: number): void => {
    this.p.set(Math.max(0.01, Math.min(0.99, value)));
  };
}

function fmt(n: number): string {
  return parseFloat(n.toFixed(2)).toString();
}

function fmt3(n: number): string {
  return parseFloat(n.toFixed(3)).toString();
}

function fmt4(n: number): string {
  return n.toFixed(4);
}
